import qs from 'qs';
import {STATUS} from '@jobseeker/common-core/constants';
import axiosInterceptorInstance from '@jobseeker/common-core/helpers/axiosInterceptor';
import {ResponseProfileV2} from './types';
import type {
  DiversityRequest,
  ResponsePro,
  ResponseProfile,
  ResponseExists,
  ReqBodyUserToken,
} from './types';
import {getMachineId} from '@jobseeker/common-core/utils/authHelper';
import axiosSimple from 'axios';

const axios = axiosInterceptorInstance(
  `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/${process.env.NEXT_PUBLIC_PREFIX_USER_SERVICE}/`
);

const profile = async (
  path: string,
  headers: object | undefined = undefined
): Promise<ResponseProfile> => {
  try {
    const response: any = await axios.get(`${path}`, {headers});
    if (response[STATUS] === 200) {
      return {
        profile: response['data'],
      };
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const checkPro = async (path: string, options = {}): Promise<ResponsePro> => {
  try {
    const response: any = await axios.get(`${path}`, options);
    if (response[STATUS] === 200) {
      return response['data'];
    }
    if (response?.type) {
      return response;
    }

    return response;
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getCoverLetter = async (): Promise<ResponseProfileV2> => {
  try {
    const response: any = await axios.get('coverletter', {
      withCredentials: true,
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    if (response[STATUS] === 200) {
      return {cl: response?.data?.data};
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getskills = async (): Promise<ResponseProfileV2> => {
  try {
    const response: any = await axios.get('skills', {
      withCredentials: true,
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    if (response[STATUS] === 200) {
      return response?.data?.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const updateFollowUp = async (
  path = 'followup',
  options = {}
): Promise<ResponsePro> => {
  try {
    const response: any = await axios.get(`${path}`, options);
    if (response[STATUS] === 200) {
      return response['data'].followed;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};
const updateCoverLetter = async (
  profileUpdationParams: ResponseProfileV2
): Promise<ResponseProfileV2> => {
  try {
    const response: any = await axios.post(
      'coverletter',
      qs.stringify({
        coverletter: profileUpdationParams.cl?.coverletter,
        includeCoverletter: profileUpdationParams.cl?.includeCoverletter,
      }),
      {
        withCredentials: true,
      }
    );
    if (response[STATUS] === 200) {
      return response?.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};
const updateSkills = async (
  skillsUpdationParams: ResponseProfileV2
): Promise<ResponseProfileV2> => {
  try {
    const response = await axios.post(
      'skills',
      {skills: skillsUpdationParams?.skillsKeys ?? []},
      {
        withCredentials: true,
      }
    );
    if (response[STATUS] === 200) {
      return response?.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};
const updateResumeStamp = async (
  isUpdate?: boolean,
  path = 'updatestamp'
): Promise<ResponsePro> => {
  try {
    const response: any = await axios.post(
      `${path}`,
      qs.stringify({
        opt: true,
        ...(isUpdate ? {isUpdate: true} : {}),
      })
    );
    if (response[STATUS] === 200) {
      return response['data'];
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const updateDiversity = async (data: Partial<DiversityRequest>) => {
  try {
    const resp = await axios.patch('diversity/maven', data, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return resp.data;
  } catch (error) {
    return {};
  }
};

const saveCookiePolicyAcceptance = async () => {
  try {
    const machineId = getMachineId();
    const response = await axios.post(
      `cookiepolicy`,
      qs.stringify({
        uniqueIdentifier: machineId,
      })
    );
    if (response[STATUS] === 200) {
      return response?.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const registerCompetition = async (path: string, info: object) => {
  try {
    const response: any = await axios.post(`${path}`, qs.stringify(info));
    if (response[STATUS] === 200) {
      return response['data'];
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const saveUserSettings = async (settings: any) => {
  try {
    const response = await axios.post(
      `settings`,
      qs.stringify({settings: JSON.stringify(settings)})
    );
    if (response[STATUS] === 200) {
      return response?.data?.settings;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getUserSettings = async () => {
  try {
    const response = await axios.get(`settings`);
    if (response[STATUS] === 200) {
      return response?.data?.settings;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getBlockedCompanies = async () => {
  try {
    const response = await axios.get(`settings/blocked-companies`);

    if (response[STATUS] === 200) {
      return response?.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const blockOrUnblockCompany = async (blockCompanyInfo: {
  name: string;
  action: 'block' | 'unblock';
}) => {
  try {
    const response = await axios.post(
      `/settings/blocked-companies`,
      qs.stringify(blockCompanyInfo)
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const twoFaEnable = async (
  twoFaInfo: {
    phone?: string;
    countryCode?: number;
    isForChangeNumber?: boolean;
  } = {}
) => {
  try {
    const response = await axios.post(
      `/twoFactorAuth/twoFaEnableCode`,
      qs.stringify(twoFaInfo)
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const sendOtp = async (otpInfo: {
  phone: string;
  countryCode: number;
  isForVerification?: boolean;
}) => {
  try {
    const response = await axios.post(
      `/twoFactorAuth/send-otp`,
      qs.stringify(otpInfo)
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const verifyContactDetails = async (
  resInfo: {
    otp?: number;
    verificationToken?: string;
  },
  verificationType: string,
  headers = {}
) => {
  try {
    const response = await axios.post(
      `/verify/${verificationType}`,
      qs.stringify(resInfo),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...headers,
        },
      }
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {message: error.response?.data?.error?.message || 'Unknown error'},
      code: error.response?.data?.error?.statusCode || 500,
    };
  }
};
const twoFaDisable = async (twoFaDisableInfo: {password: string}) => {
  try {
    const response = await axios.post(
      `/twoFactorAuth/disableTwoAuth`,
      qs.stringify(twoFaDisableInfo)
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};
const twoFaVerify = async (twoFaVerifyInfo: {
  phone: string;
  countryCode: number;
  otp: number;
  isForChangeNumber?: boolean;
}) => {
  try {
    const response = await axios.post(
      `/twoFactorAuth/verify-two-fa`,
      qs.stringify(twoFaVerifyInfo)
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const sendVerifyEmail = async () => {
  try {
    const response = await axios.post(
      `/resend_email_verification`,
      qs.stringify({})
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const getS3Resource = async (name: string) => {
  try {
    const response = await axiosSimple.get(
      `/api/s3_resource/${encodeURIComponent(name)}`
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const getPromoBanners = async (columnName: string) => {
  try {
    const response = await axios.get(`/read-csv/${columnName}`);

    if (response.status === 200) {
      return response?.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message:
          error.response?.data?.error?.message ||
          'Something went wrong. Please try again later.',
        code: error?.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const changePassword = async (changePasswordData: {
  password: string;
  confirmpassword: string;
  oldpassword: string;
}) => {
  try {
    const response = await axios.post(
      `/change_password`,
      qs.stringify(changePasswordData)
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
        name: error.response?.data?.error?.name || 'unknown_error',
      },
    };
  }
};

const changeEmail = async (changeEmailData: {
  password: string;
  newEmail: string;
}) => {
  try {
    const response = await axios.post(
      `/change_email`,
      qs.stringify(changeEmailData)
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const cancelEmailChange = async () => {
  try {
    const response = await axios.post(`/cancel_email_change`, qs.stringify({}));

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const resentChangeEmail = async () => {
  try {
    const response = await axios.post(
      `/resend_verification_email`,
      qs.stringify({})
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const verifyChangeEmail = async (requestData: {
  verificationToken: string;
  securityId: string;
}) => {
  try {
    const response = await axios.post(
      `/verify_email`,
      qs.stringify(requestData)
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const deactivateAccount = async (deactivateInfo: {
  password: string;
  reason: string;
  textMsg?: string;
}) => {
  try {
    const response = await axios.post(
      `/deactivate`,
      qs.stringify(deactivateInfo)
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};
const checkExists = async (email: string): Promise<ResponseExists> => {
  try {
    const response: any = await axios.get(`exists?email=${email}`);
    if (response[STATUS] === 200) {
      return response['data'];
    }

    return response;
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const downloadCaseStudy = async (
  round: number,
  competitionId: number
): Promise<any> => {
  try {
    const response: any = await axios.get(
      `competition/download?round=${round}&competitionId=${competitionId}`
    );
    if (response[STATUS] === 200) {
      return response['data'];
    }

    return response;
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getAuth = async (
  reqBodyParams: ReqBodyUserToken
): Promise<ResponseExists> => {
  try {
    const response = await axios.post(`getauth`, qs.stringify(reqBodyParams), {
      headers: {
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_LONGLIVE_TOKEN}`,
        'Content-Type': 'application/x-www-form-urlencoded',
        'User-Agent':
          'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0.0.0 Safari/537.36',
      },
    });
    if (response[STATUS] === 200) {
      return response?.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const updatePersonalizedTags = async (tagInfo: object) => {
  try {
    const response: any = await axios.post(
      `/profile/tags`,
      qs.stringify({
        tagInfo: JSON.stringify(tagInfo),
      })
    );
    if (response[STATUS] === 200) {
      return response['data'];
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const updateCompetition = async (path: string, data: object): Promise<any> => {
  try {
    const response = await axios.post(`${path}`, qs.stringify(data));

    if (response.status === 200) {
      return response?.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const handleConsent = async (data: {token: string}): Promise<any> => {
  try {
    const response = await axios.post('consent', qs.stringify(data));

    if (response.status === 200) {
      return response.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message:
          error.response?.data?.error?.message || error.response?.data?.message,
        code: error.response?.data?.error?.statusCode || error.response?.status,
      },
    };
  }
};

const sendTwoFactorLoginOtp = async (otpInfo: {
  otpBy: number;
  forWhat: string;
  seekerToken: string;
}) => {
  try {
    if (!otpInfo.seekerToken) {
      return {
        error: {
          message: 'Try again!',
          code: 0,
        },
      };
    }
    const response = await axios.post(
      `/twoFactorAuth/send-otp`,
      qs.stringify(otpInfo)
    );

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const verifyTwoFactorLoginOtp = async (otpInfo: {
  forWhat: string;
  otp: string;
  seekerToken: string;
  phone: string;
  countryCode: number;
}) => {
  try {
    if (!otpInfo.seekerToken) {
      return {
        error: {
          message: 'Try again!',
          code: 0,
        },
      };
    }
    const response = await axios.post(
      `/twoFactorAuth/verify-two-fa`,
      qs.stringify(otpInfo)
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const trackJobfeedInterest = async (data: {
  email: string;
  jobId: string;
  sessionId: string;
}) => {
  try {
    const response = await axios.post(`/subscribe-jobfeed`, qs.stringify(data));

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const appleLogin = async (payload: {code: string}) => {
  try {
    const response = await axios.post(
      `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/user/auth/appleLogin`,
      {params: {code: payload.code}},
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    return response.data;
  } catch (error: any) {
    return {
      action: error?.response?.data?.action,
      error: {
        message: error?.response?.data?.error?.message || 'Unknown error',
        code:
          error?.response?.data?.error?.statusCode ||
          error?.response?.status ||
          500,
      },
    };
  }
};

const socialLogin = async (payload: {code?: string; token?: string}) => {
  try {
    const body = payload?.code
      ? {params: {code: payload?.code}}
      : {token: payload?.token};

    const response = await axios.post(
      `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/user/auth/socialLogin`,
      body,
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    return response.data;
  } catch (error: any) {
    return {
      error: {
        message: error?.response?.data?.error?.message || 'Unknown error',
        code:
          error?.response?.data?.error?.statusCode ||
          error?.response?.status ||
          500,
      },
    };
  }
};

const cohortEligible = async (cohort: string, headers: any): Promise<any> => {
  try {
    const response = await axios.post(
      `eligible`,
      qs.stringify({cohortKey: cohort}),
      {
        ...headers,
        headers: {
          ...(headers?.headers || {}),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    if (response.status === 200) {
      return response?.data?.data;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data?.error?.message || 'Unknown error',
        code: error.response?.data?.error?.statusCode || 500,
      },
    };
  }
};

const userService = {
  verifyChangeEmail,
  deactivateAccount,
  resentChangeEmail,
  cancelEmailChange,
  changePassword,
  profile,
  checkPro,
  changeEmail,
  updateFollowUp,
  updateResumeStamp,
  updateDiversity,
  saveCookiePolicyAcceptance,
  registerCompetition,
  saveUserSettings,
  getUserSettings,
  getBlockedCompanies,
  blockOrUnblockCompany,
  twoFaEnable,
  sendOtp,
  twoFaDisable,
  twoFaVerify,
  checkExists,
  downloadCaseStudy,
  getS3Resource,
  getPromoBanners,
  verifyContactDetails,
  sendVerifyEmail,
  getAuth,
  updatePersonalizedTags,
  updateCoverLetter,
  updateSkills,
  getCoverLetter,
  getskills,
  updateCompetition,
  handleConsent,
  sendTwoFactorLoginOtp,
  trackJobfeedInterest,
  verifyTwoFactorLoginOtp,
  socialLogin,
  appleLogin,
  cohortEligible,
};

export default userService;
